import { Box, HStack, Heading, useColorModeValue } from "@chakra-ui/react";
import { CellClassParams, ColDef, IRowNode } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { PatientQueryParamProps } from "../../shared-logic/consolidated-context/reducer";
import { NoPatientData } from "../no-data";
import { TableGrid } from "../table-grid";
import DropdownFilter from "./dropdown-filter";
import ToggleFilter from "./toggle-filter";

export interface FilterToggle {
  label: string;
  groupLabel?: string;
  isChecked?: boolean;
  filterFn: (rowData: IRowNode["data"]) => boolean;
  sortFn?: (a: IRowNode["data"], b: IRowNode["data"]) => number;
}

export interface FhirSectionProps {
  sectionId: string;
  sectionName: string;
  rowData: object[];
  defaultColDef?: ColDef;
  columnDefs: ColDef[];
  filterToggles?: FilterToggle[];
  denseDisplay?: boolean;
  patientQueryParams?: PatientQueryParamProps | undefined;
  usingSectionSearch?: boolean;
  usingMetriportOnlyBundleFromConsolidated?: boolean;
  handleCellClick: (sectionId: string, event: { data: object }) => void;
  handleCellStyle: (params: CellClassParams) => { backgroundColor: string } | undefined;
}

function FhirSection({
  sectionId, // same as key
  sectionName,
  rowData,
  defaultColDef,
  columnDefs,
  patientQueryParams,
  filterToggles,
  handleCellClick,
  denseDisplay = false,
  usingSectionSearch = false,
  usingMetriportOnlyBundleFromConsolidated = false,
}: FhirSectionProps) {
  const [filters, setFilters] = useState<FilterToggle[]>([]);
  // track the last activated filter to trigger sortFn if exists
  const [activeFilters, setActiveFilters] = useState<FilterToggle[]>([]);

  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    if (!filterToggles) return;
    setFilters(filterToggles);
    setActiveFilters(filterToggles.filter(filter => filter.isChecked));
  }, [filterToggles]);

  useEffect(() => {
    const lastActiveFilter = activeFilters[activeFilters.length - 1];
    if (lastActiveFilter?.sortFn) {
      gridRef.current?.api?.applyColumnState({ defaultState: { sort: null } });
    }
  }, [activeFilters]);

  useEffect(() => {
    const sectionKeyParam = patientQueryParams?.sectionKey;
    const resourceIdParam = patientQueryParams?.resourceId;
    if (sectionKeyParam && resourceIdParam && sectionKeyParam === sectionId) {
      const selectedRow = rowData.find((row: { id?: string }) => row.id === resourceIdParam);
      if (selectedRow) {
        handleCellClick(sectionId, { data: selectedRow });
      }
    }
  }, []);

  function toggleFilter(label: string, isChecked: boolean) {
    if (isChecked) {
      const newFilter = filters.find(f => f.label === label);
      if (newFilter) setActiveFilters(prev => [...prev, newFilter]);
    } else {
      setActiveFilters(prev => prev.filter(f => f.label !== label));
    }
  }

  const filteredRowData = useMemo(() => {
    if (!activeFilters.length) return rowData;

    // Group filters by their groupLabel to enable OR within groups, AND between groups
    const filtersByGroup = activeFilters.reduce((acc, filter) => {
      const group = filter.groupLabel ?? "default";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group]?.push(filter);
      return acc;
    }, {} as Record<string, FilterToggle[]>);

    // Apply filters with OR logic within groups, AND logic between groups
    const filtered = rowData.filter(data => {
      return Object.values(filtersByGroup).every(groupFilters => {
        return groupFilters.some(filter => filter.filterFn(data));
      });
    });

    const lastActiveFilter = activeFilters[activeFilters.length - 1];
    if (lastActiveFilter?.sortFn) return filtered.sort(lastActiveFilter.sortFn);
    return filtered;
  }, [activeFilters, rowData]);

  const isDropdownFilter = filters.every(filter => filter.groupLabel);

  return (
    <Box
      id={sectionId}
      w={"100%"}
      mb={5}
      borderRadius={10}
      bg={useColorModeValue("white", "gray.800")}
      px={7}
      py={5}
      border="solid"
      borderColor={useColorModeValue("gray.200", "gray.700")}
    >
      <HStack mb={5} w={"100%"} justifyContent={"space-between"}>
        <Heading size={denseDisplay ? "xs" : "md"}>{sectionName}</Heading>
        {filters.length === 0 ? null : isDropdownFilter ? (
          <DropdownFilter
            filters={filters}
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
          />
        ) : (
          <ToggleFilter
            filters={filters}
            activeFilters={activeFilters}
            toggleFilter={toggleFilter}
          />
        )}
      </HStack>
      {filteredRowData.length > 0 ? (
        <TableGrid
          ref={gridRef}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={filteredRowData}
          usingSectionSearch={usingSectionSearch}
        />
      ) : (
        <NoPatientData
          message={
            usingMetriportOnlyBundleFromConsolidated ? "EHR is in sync" : "No Data Available"
          }
        />
      )}
    </Box>
  );
}

export default FhirSection;
